package com.flowyride.core.domain.model

data class TrackPoint(
    val point: GeoPoint,
    val elevationMeters: Double? = null,
)

/**
 * The part of a ride between two consecutive waypoints.
 *
 * @param lastTrackIndex Index in [Route.track] of this leg's last point (the next waypoint).
 */
data class RouteLeg(
    val distanceMeters: Double,
    val durationSeconds: Double,
    val lastTrackIndex: Int,
)

/**
 * A stretch of the ride with a known speed limit, from track index [startTrackIndex] to [endTrackIndex].
 * Stretches without section have no known limit.
 */
data class SpeedLimitSection(
    val startTrackIndex: Int,
    val endTrackIndex: Int,
    val speedLimitKmh: Int,
)

/** A computed ride, independent from the engine that produced it. */
data class Route(
    val engineId: RouteEngineId,
    val track: List<TrackPoint>,
    val distanceMeters: Double,
    val durationSeconds: Double,
    /** One per pair of consecutive waypoints; empty when the engine does not report them. */
    val legs: List<RouteLeg> = emptyList(),
    /** Turn-by-turn instructions, ordered along the track; empty when the engine does not give them. */
    val maneuvers: List<Maneuver> = emptyList(),
    /** Legal speed limits along the track, in order; empty when the engine does not give them. */
    val speedLimits: List<SpeedLimitSection> = emptyList(),
) {
    /** Index of the leg that contains [trackIndex] (0 when legs are unknown). */
    fun legIndexAt(trackIndex: Int): Int =
        legs.indexOfFirst { trackIndex < it.lastTrackIndex }.takeIf { it >= 0 } ?: legs.lastIndex.coerceAtLeast(0)
}
